import React, { useState, useEffect } from 'react';
import { Calendar, User, Activity, Loader2, Clock } from 'lucide-react';
import * as WizardService from '@/services/appointment-wizard-service';
import { fetchSpecialtiesByClinic } from '@/services/dental-specialty-service';
import { Button } from '@/components/ui/button';
import { SchedulingWizardCore } from './SchedulingWizardCore';
import { useToast } from '@/hooks/use-toast';
import { useClinic } from '@/contexts/ClinicContext';
import type { WizardAvailableSlot } from '@/services/appointment-wizard-service';

interface Props {
  patient: any;
  originalAppointmentId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const WizardAgendamento: React.FC<Props> = ({
  patient,
  originalAppointmentId,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [specialties, setSpecialties] = useState<any[]>([]);
  const { toast } = useToast();
  const { selectedClinicId } = useClinic();

  const [specialtyId, setSpecialtyId] = useState('');
  const [doubleSlot, setDoubleSlot] = useState(false);

  // clinicId: usa contexto; fallback para vazio enquanto carrega
  const clinicId = selectedClinicId ?? '';

  useEffect(() => {
    if (!clinicId) return;
    setLoading(true);
    fetchSpecialtiesByClinic(clinicId)
      .then(data => setSpecialties(data))
      .catch(() => toast({ title: 'Erro', description: 'Falha ao carregar especialidades desta unidade.', variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, [clinicId, toast]);

  const handleConfirm = async (slot: WizardAvailableSlot) => {
    if (!slot.slot_id) {
      toast({ title: 'Erro', description: 'Horário não selecionado corretamente.', variant: 'destructive' });
      return;
    }
    setConfirming(true);
    try {
      const slot_ids = slot.double_slot && slot.slot_id_b
        ? [slot.slot_id, slot.slot_id_b]
        : [slot.slot_id];

      const payload: WizardService.WizardConfirmRequest = {
        patient_id: patient?.id,
        slot_ids,
        original_appointment_id: originalAppointmentId || undefined,
        dental_insurance_id: patient?.dental_insurance_id || undefined,
        card_number: patient?.card_number || undefined,
      };

      await WizardService.confirmWizardBooking(payload);
      toast({ title: 'Sucesso!', description: 'Consulta agendada com sucesso.' });
      onSuccess();
      onClose();
    } catch (err: any) {
      toast({ title: 'Erro de Salvamento', description: err.message || 'Verifique os dados do paciente.', variant: 'destructive' });
    } finally {
      setConfirming(false);
    }
  };

  if (!patient) {
    return (
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[100]">
        <div className="bg-white p-6 rounded-xl shadow-xl flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
          <p className="text-sm font-bold text-slate-600">Aguardando dados do paciente...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">

        {/* Header */}
        <div className="bg-slate-50 px-4 py-2.5 border-b flex justify-between items-center shrink-0">
          <div className="flex items-center gap-2">
            <div className="bg-teal-100 p-1.5 rounded-full text-teal-600">
              {(loading || confirming) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar className="h-4 w-4" />}
            </div>
            <h2 className="text-lg font-bold text-slate-800">Agendamento Assistido</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded-full">✕</button>
        </div>

        <div className="p-4">
          {/* Paciente */}
          <div className="flex items-center gap-2 mb-4 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
            <User className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-500">Paciente:</span>
            <span className="text-sm font-bold text-slate-900">{patient?.name}</span>
          </div>

          {/* ── Step 1: Especialidade + Duração ── */}
          {step === 1 && (
            <div className="space-y-3">
              <label className="text-xs font-bold text-slate-400 uppercase flex items-center gap-2">
                <Activity className="h-3 w-3" /> 1. Escolha a Especialidade
              </label>

              <select
                className="w-full border border-slate-200 rounded-lg p-3 bg-white shadow-sm outline-none focus:ring-2 focus:ring-teal-500"
                disabled={loading}
                value={specialtyId}
                onChange={e => setSpecialtyId(e.target.value)}
              >
                <option value="">Selecione uma especialidade...</option>
                {specialties.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>

              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-400 uppercase flex items-center gap-2">
                  <Clock className="h-3 w-3" /> Duração da consulta
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[{ value: false, label: '30', sub: 'minutos' }, { value: true, label: '1h', sub: 'hora' }].map(opt => (
                    <button
                      key={String(opt.value)}
                      type="button"
                      onClick={() => setDoubleSlot(opt.value)}
                      className={`flex flex-col items-center gap-0.5 p-2 rounded-xl border-2 font-bold text-sm transition-all ${
                        doubleSlot === opt.value
                          ? 'border-teal-500 bg-teal-50 text-teal-700'
                          : 'border-slate-200 text-slate-500 hover:border-slate-300'
                      }`}
                    >
                      <span className="text-xl font-black">{opt.label}</span>
                      <span className="text-[10px] uppercase tracking-widest">{opt.sub}</span>
                    </button>
                  ))}
                </div>
              </div>

              <Button
                onClick={() => setStep(2)}
                disabled={!specialtyId || loading}
                className="w-full bg-teal-600 hover:bg-teal-700 py-4 text-base font-bold"
              >
                {loading ? <Loader2 className="animate-spin mr-2" /> : null}
                Ver Datas Disponíveis
              </Button>
            </div>
          )}

          {/* ── Step 2: Core (calendário → horários → confirmação) ── */}
          {step === 2 && (
            <SchedulingWizardCore
              key={`${specialtyId}-${doubleSlot}`}
              clinicId={clinicId}
              specialtyId={specialtyId}
              specialtyName={specialties.find(s => s.id === specialtyId)?.name ?? ''}
              doubleSlot={doubleSlot}
              fetchDays={WizardService.fetchWizardAvailableDays}
              fetchSlots={WizardService.fetchWizardAvailableSlots}
              onConfirm={handleConfirm}
              confirming={confirming}
              onBack={() => setStep(1)}
              backLabel="Voltar para especialidades"
              onError={msg => toast({ title: 'Erro', description: msg, variant: 'destructive' })}
            />
          )}
        </div>
      </div>
    </div>
  );
};
